// components/ProjectsTable.jsx

import React, { useState, useRef, useEffect } from "react";
import { Eye, Pencil, Download, ListChecks, PlusCircle } from "lucide-react";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ViewProjectModal from "./ViewProjectModal";
import EditProjectModal from "./EditProjectModal";
import RequirementsModal from "./RequirementModal";
import KPIListModal from "./programManager/MilestoneListModal";

export default function ProjectsTable({
    projects = [],
    onSuccess,
}) {
    // State hooks for component logic
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const projectsPerPage = 10;
    const dropdownRef = useRef(null);

    // State for managing modals
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [isRequirementsModalOpen, setIsRequirementsModalOpen] = useState(false);
    const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);

    // Filter projects based on the search term, and handle pagination
    const filteredProjects = projects.filter((project) =>
        project.projectTitle.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLast = currentPage * projectsPerPage;
    const indexOfFirst = indexOfLast - projectsPerPage;
    const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setExportDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- HANDLERS FOR MODALS ---

    const handleView = (projectId) => {
        setSelectedProjectId(projectId);
        setIsViewModalOpen(true);
    };

    const handleEdit = (projectId) => {
        setSelectedProjectId(projectId);
        setIsEditModalOpen(true);
    };

    const handleOpenRequirements = (project) => {
        setSelectedProject(project);
        setIsRequirementsModalOpen(true);
    };

    const handleOpenMilestones = (project) => {
        setSelectedProject(project);
        setIsMilestoneModalOpen(true);
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedProjectId(null);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedProjectId(null);
        if (onSuccess) onSuccess();
    };

    const closeRequirementsModal = () => {
        setIsRequirementsModalOpen(false);
        setSelectedProject(null);
    };

    const closeMilestoneModal = () => {
        setIsMilestoneModalOpen(false);
        setSelectedProject(null);
    };


    // --- EXPORT FUNCTIONALITY ---

    const mapProjectToExportRow = (project) => ({
        "Project Title": project.projectTitle,
        "Project ID": project._id,
        "Start Date": format(new Date(project.startDate), "yyyy-MM-dd"),
        "End Date": format(new Date(project.endDate), "yyyy-MM-dd"),
        "Location": project.location,
        "Donors": Array.isArray(project.donor)
            ? project.donor.map((d) => d.name).join(", ")
            : project.donor?.name || "N/A",
        "Categories": project.projectCategories?.join(", ") || "N/A",
        "Status": project.status,
        "Created At": format(new Date(project.createdAt), "yyyy-MM-dd"),
    });

    const handleExport = async (format) => {
        setExportDropdownOpen(false);
        setIsExporting(true);

        const exportData = filteredProjects.map(mapProjectToExportRow);

        if (exportData.length === 0) {
            alert("No data available to export.");
            setIsExporting(false);
            return;
        }

        switch (format) {
            case "excel": {
                const worksheet = XLSX.utils.json_to_sheet(exportData);
                const colWidths = Object.keys(exportData[0]).map((key) => ({
                    wch: Math.max(key.length, 20),
                }));
                worksheet["!cols"] = colWidths;
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
                XLSX.writeFile(workbook, "Projects_Export.xlsx");
                break;
            }
            case "pdf": {
                const doc = new jsPDF({ orientation: "landscape" });
                autoTable(doc, {
                    head: [Object.keys(exportData[0])],
                    body: exportData.map((row) => Object.values(row)),
                    headStyles: { fillColor: [237, 125, 49] },
                    didDrawPage: (data) => {
                        doc.text("Project Report", 20, 15);
                    },
                });
                doc.save("Projects_Report.pdf");
                break;
            }
            case "csv": {
                const worksheet = XLSX.utils.json_to_sheet(exportData);
                const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
                const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.setAttribute("download", "Projects_Export.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                break;
            }
            default:
                break;
        }
        setIsExporting(false);
    };


    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

        return (
            <div className="flex justify-center mt-4 space-x-2">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                >
                    Previous
                </button>
                {pageNumbers.map((page) => (
                    <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 rounded border ${currentPage === page
                                ? "bg-orange-500 text-white"
                                : "bg-gray-100 text-gray-600"
                            }`}
                    >
                        {page}
                    </button>
                ))}
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        );
    };

    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Project List</h2>
            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="border rounded px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <div className="flex space-x-2">
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                            disabled={isExporting}
                            className="inline-flex items-center gap-2 justify-center rounded bg-orange-500 px-4 py-2 text-white hover:bg-orange-600 focus:outline-none disabled:bg-orange-300"
                        >
                            {isExporting ? (
                                "Exporting..."
                            ) : (
                                <>
                                    <Download size={16} /> Export
                                </>
                            )}
                        </button>
                        {exportDropdownOpen && (
                            <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                                <div className="py-1">
                                    {["Excel", "PDF", "CSV"].map((format) => (
                                        <button
                                            key={format}
                                            onClick={() => handleExport(format.toLowerCase())}
                                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100"
                                        >
                                            Export as {format}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead className="bg-orange-100">
                        <tr>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">Title</th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">Location</th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">Donor</th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">Category</th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">Start Date</th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">End Date</th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">Status</th>
                            <th className="p-3 text-center text-sm font-semibold text-orange-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentProjects.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="p-4 text-center text-gray-500">No projects found.</td>
                            </tr>
                        ) : (
                            currentProjects.map((project) => (
                                <tr key={project._id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="p-3 whitespace-nowrap font-medium">{project.projectTitle}</td>
                                    <td className="p-3 whitespace-nowrap">{project.location}</td>
                                    <td className="p-3 whitespace-nowrap">
                                        {Array.isArray(project.donor)
                                            ? project.donor.map((d) => d.name).join(", ")
                                            : project.donor?.name || "N/A"}
                                    </td>
                                    <td className="p-3 whitespace-nowrap capitalize">
                                        {project.projectCategories?.join(", ")}
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        {new Date(project.startDate).toLocaleDateString()}
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        {new Date(project.endDate).toLocaleDateString()}
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${project.status === "Ongoing" ? "bg-green-100 text-green-800" : project.status === "Completed" ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"}`}>
                                            {project.status?.toUpperCase() || "UNKNOWN"}
                                        </span>
                                    </td>
                                    <td className="p-3 whitespace-nowrap text-center">
                                        <div className="flex items-center justify-center space-x-2">
                                            <button
                                                onClick={() => handleView(project._id)}
                                                className="p-2 text-blue-600 hover:text-blue-800"
                                                title="View Details"
                                            >
                                                <Eye size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(project._id)}
                                                className="p-2 text-orange-600 hover:text-orange-800"
                                                title="Edit Project"
                                            >
                                                <Pencil size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleOpenRequirements(project)}
                                                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-1.5 px-3 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 text-sm"
                                                title="Manage Requirements"
                                            >
                                                Requirements
                                            </button>
                                            <button
                                                onClick={() => handleOpenMilestones(project)}
                                                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-1.5 px-3 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 text-sm flex items-center gap-1"
                                                title="Manage KPIs"
                                            >
                                                <ListChecks size={14} />
                                                KPIs
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {renderPagination()}

            {/* MODAL COMPONENTS */}
            {isViewModalOpen && (
                <ViewProjectModal
                    isOpen={isViewModalOpen}
                    onClose={closeViewModal}
                    projectId={selectedProjectId}
                />
            )}
            <EditProjectModal isOpen={isEditModalOpen} onClose={closeEditModal} projectId={selectedProjectId} onSuccess={onSuccess} />
            {isRequirementsModalOpen && selectedProject && (
                <RequirementsModal
                    projectId={selectedProject._id}
                    projectName={selectedProject.projectTitle}
                    projectBudgetCode={selectedProject.budgetCode}
                    onClose={closeRequirementsModal}
                />
            )}
            {isMilestoneModalOpen && selectedProject && (
                <KPIListModal
                    isOpen={isMilestoneModalOpen}
                    onClose={closeMilestoneModal}
                    project={selectedProject}
                />
            )}
        </div>
    );
}